import {
  type EditAreaTemplateGeometry,
  FACE_KEYS,
  type FaceKey,
  PRODUCT_MEASUREMENT_KIND_LABELS,
  type ProductMeasurementKind,
  type SizeMm,
} from '@repo/api-contracts';

const FACE_LABELS: Readonly<Record<FaceKey, string>> = {
  front: '앞면',
  back: '뒷면',
  left: '왼쪽',
  right: '오른쪽',
};

const SHAPE_PREFIXES = { ellipse: '원형 ', polygon: '다각형 ' } as const;

const mm = (value: number): string => `${Math.round(value * 10) / 10}`;

export interface TemplateGeometrySummary {
  /** "원통 · 지름 85 × 높이 230mm" */
  readonly measurement: string;
  /** 인쇄 영역이 배치된 면과 그 면의 인쇄 영역 — "앞면: 랩 인쇄 120×150mm". */
  readonly faces: readonly string[];
  /** 서피스·배치가 없는 면(사진만 받는 면) 이름. */
  readonly photoOnlyFaces: readonly string[];
}

/** 템플릿 기하 요약 — 목록·적용 버튼이 무엇이 들어 있는지 보여 줄 때 쓴다. */
export const describeTemplateGeometry = (
  geometry: EditAreaTemplateGeometry,
  measurementKind: ProductMeasurementKind,
  measurement: SizeMm,
): TemplateGeometrySummary => {
  const names = new Map(geometry.components.map((component) => [component.id, component]));
  const faces: string[] = [];
  const photoOnlyFaces: string[] = [];
  for (const key of FACE_KEYS) {
    const face = geometry.faces[key];
    if (face === undefined) continue;
    if (face.placements.length === 0) {
      photoOnlyFaces.push(FACE_LABELS[key]);
      continue;
    }
    const areas = face.placements.flatMap((placement) => {
      const component = names.get(placement.componentId);
      return component === undefined
        ? []
        : [
            `${component.name} ${component.shape ? SHAPE_PREFIXES[component.shape.type] : ''}${mm(component.widthMm)}×${mm(component.heightMm)}mm`,
          ];
    });
    faces.push(`${FACE_LABELS[key]}: ${areas.join(', ')}`);
  }
  const width = measurementKind === 'CYLINDER' ? '지름' : '넓이';
  return {
    measurement: `${PRODUCT_MEASUREMENT_KIND_LABELS[measurementKind]} · ${width} ${mm(measurement.widthMm)} × 높이 ${mm(measurement.heightMm)}mm`,
    faces,
    photoOnlyFaces,
  };
};
